import type { DefaultOptions } from '@tanstack/vue-query';
import { QueryClient } from '@tanstack/vue-query';
import type { AxiosError } from 'axios';
import { isGatewayTimeout } from '@/utils/errorMessage';

/**
 * Shared TanStack Query defaults for the app and for tests that construct a QueryClient.
 *
 * We used to use the library defaults, notably `staleTime: 0` and `refetchOnWindowFocus: true`.
 * As of October 2026 these match the options we use most often in the platform.
 *
 * Other library defaults: https://tanstack.com/query/latest/docs/framework/vue/guides/important-defaults
 *
 * `refetchOnWindowFocus` runs in the same turn Chrome unfreezes a tab, before the first paint.
 * A focus refetch re-renders every active observer and the tab stays blank for seconds.
 */
export const appQueryDefaultOptions: DefaultOptions = {
  queries: {
    retry: (failureCount: number, error) => {
      if (isGatewayTimeout(error)) {
        return false;
      }

      if (failureCount >= 3) return false;

      if (((error as AxiosError).response?.status as number) >= 500) return false;

      return true;
    },
    // eslint-disable-next-line no-restricted-syntax -- these lines are the app defaults
    staleTime: Number.POSITIVE_INFINITY,
    // eslint-disable-next-line no-restricted-syntax -- these lines are the app defaults
    refetchOnWindowFocus: false,
  },
};

export function createAppQueryClient(defaultOptions?: DefaultOptions): QueryClient {
  return new QueryClient({
    defaultOptions: {
      ...appQueryDefaultOptions,
      ...defaultOptions,
      queries: {
        ...appQueryDefaultOptions.queries,
        ...defaultOptions?.queries,
      },
    },
  });
}
